import * as React from "react";
import { useEffect, useState } from "react";
import { ApplicationFeature, permissionGroupHelper, PublicAmorgUser, UserSettings } from '../../../amorg-common';
import { AmorgUserRole } from '../../../amorg-common';
import { AmorgRole } from '../../../amorg-common';
import { AdminPasswordEditor, PasswordEditor } from "./PasswordEditor";
import { EmailEditor } from "./EmailEditor";
import { RoleEditor } from "./RoleEditor";
import { PermissionGroupEditor } from './PermissionGroupEditor';
import { UserDetailsEditor } from "./UserDetailsEditor";
import { permissionHelper } from '../../../amorg-common';
import { Flex, FlexItem } from '../../../designSystem/compositeComponents/FlexHelpers';
import { getUser, getRoles, getUserRoles, updateUserSettings, getLastUserIntroEmailSent, sendUserIntroEmail } from '../../../api/userClientApi';
import { Card } from "../../../designSystem/Card";
import { Checkbox } from "../../../designSystem/inputs/Checkbox";
import { Modal } from "../../../designSystem/Modal";
import { Button } from "../../../designSystem/button/Button";
import { ButtonRow } from "../../../designSystem/button/ButtonRow";
import { toDateTimeString } from '../../../utils/date';
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { HtmlEditor } from "../../../designSystem/inputs/HtmlEditor";
import { Small } from "../../../designSystem/typography/Body";
import { ToggletipTheOne } from "../../../designSystem/ToggletipTheOne";
import { getMaybeActiveUserOrRefresh } from "../../../api/serverApiHelpers";
import { getActiveUser } from "../../../api/authClientApi";

type UserEditorProps = {
    activeUser: PublicAmorgUser;
    targetUserId: string;
    onSelectedUserUpdated: () => void;
};
type UserEditorState = {
    activeUserFeatures:ApplicationFeature[];
    siteId:string;
    targetUser?: PublicAmorgUser;
    roles: AmorgRole[];
    targetUserRoles: AmorgUserRole[];
    inEditUserRole?: AmorgUserRole;
};
export default function UserEditor ({ activeUser, targetUserId, onSelectedUserUpdated }: UserEditorProps): JSX.Element
{

    const [data, setData] = useState<UserEditorState>({
        activeUserFeatures: [],
        siteId: '',
        targetUser: undefined,
        roles: [],
        targetUserRoles: [],
        inEditUserRole: null,
    });

    const getData = async () =>
    {
        const activeTokenUser = await getMaybeActiveUserOrRefresh();
        const activeUserFeatures = activeTokenUser.hasAccess ? activeTokenUser.tokenUser.features : [];
        const siteId = activeTokenUser.hasAccess ? activeTokenUser.tokenUser.siteId : '';
        const targetUser = activeUser.id === targetUserId 
            ? await getActiveUser() 
            : await getUser(targetUserId);

        const roles = permissionGroupHelper.hasMinimumPermissionGroup(activeUser.permissionGroup.id, 'member')
            ? await getRoles()
            : [];

        const targetUserRoles = 
                permissionGroupHelper.hasMinimumPermissionGroup(activeUser.permissionGroup.id, 'member')
                ? await getUserRoles(targetUserId)
                : [];
        setData(
            {
                activeUserFeatures,
                siteId: siteId,
                targetUser: targetUser,
                roles,
                targetUserRoles: targetUserRoles,
                inEditUserRole: null
            }
        );
    };

    useEffect(() =>
    {
        getData();
    }, [activeUser.permissionGroup.id]);
    
    async function handleSelectedUserUpdated ()
    {
        getData();
        onSelectedUserUpdated();
    }

    async function handleSettingsChange (settings: UserSettings)
    {
        await updateUserSettings(data.targetUser.id, settings);
        await handleSelectedUserUpdated();
    }

    const canAdminEdit = data.targetUser
        ? permissionHelper.canAdminEditTargetGroupId(activeUser.permissionGroup.id, data.targetUser.permissionGroup.id, activeUser.id === data.targetUser.id)
        : false;

    return (

        <Flex justifyContent={"center"}>
            <Flex gap={32} flexDirection="column" width="75%" style={{ minWidth: '300px', marginBottom: '50px' }}>
                {data.targetUser &&
                    <>

                        <UserDetailsEditor
                            user={data.targetUser}
                            onUserUpdated={handleSelectedUserUpdated} />

                        <Flex gap={16} flexDirection="row">
                            <FlexItem width="50%">
                                <Card variant="outlined">
                                    <EmailEditor
                                        siteId={data.siteId}
                                        selectedUser={data.targetUser}
                                        isAdmin={canAdminEdit} />
                                </Card>
                            </FlexItem>
                            <FlexItem width="50%">
                                <Card variant="outlined">
                                    {canAdminEdit
                                        ? <AdminPasswordEditor selectedUser={data.targetUser} />
                                        : <PasswordEditor selectedUser={data.targetUser} />
                                    }
                                </Card>
                            </FlexItem>
                        </Flex>
                    </>
                }


                <Card variant="outlined">
                    {data.targetUser &&
                        <PermissionGroupEditor
                            activeUser={activeUser}
                            targetUser={data.targetUser}
                            onSelectedUserUpdated={handleSelectedUserUpdated} />
                    }
                </Card>


                {data?.activeUserFeatures.includes('Articles') &&
                    <Card variant="outlined">
                        <Flex>
                            <Checkbox
                                text="Lovservice kontaktperson"
                                checked={data.targetUser?.settings.articleContact ?? false}
                                value="articleContact"
                                onChange={(_, checked) => handleSettingsChange({ ...data.targetUser?.settings, articleContact: checked })}
                                disabled={!data.targetUser?.enabled}
                            />
                            <ToggletipTheOne horizontalAlignment="right" verticalAlignment="top">
                                <p>En lovservice kontaktperson modtager notifikationer om nye eller opdaterede artikler fra lovservice.</p>
                            </ToggletipTheOne>
                        </Flex>
                    </Card>
                }


                {data.targetUser && permissionGroupHelper.hasMinimumPermissionGroup(data.targetUser.permissionGroup.id, 'member') &&
                    <RoleEditor userId={data.targetUser.id} disabled={!data.targetUser.enabled} />
                }

                {data.targetUser?.enabled && permissionGroupHelper.hasMinimumPermissionGroup(activeUser.permissionGroup.id, 'siteadmin') &&
                    <UserEmailModalWithButton siteId={data.siteId} selectedUser={data.targetUser} />
                }

            </Flex>
        </Flex>
    );
}


type UserEmailModalWithButtonProps = {
    siteId:string,
    selectedUser?: PublicAmorgUser
}
function UserEmailModalWithButton(props:UserEmailModalWithButtonProps):JSX.Element
{
    const [loading, setLoading] = React.useState(false);
    const [showModal, setShowModal] = React.useState(false);
    const [lastEmailSent, setLastEmailSent] = React.useState<Date | null>(null);
    const [message, setMessage] = React.useState<string>('');

    React.useEffect(()=>{
        refreshLastEmailSent();
    }, [props.selectedUser?.id]);

    async function refreshLastEmailSent()
    {
        if(props.selectedUser)
        {
            const emailSent = await getLastUserIntroEmailSent(props.selectedUser?.id);
            setLastEmailSent(emailSent);
        }
    }

    function handleClickOpen()
    {
        setMessage(
            `<p>Hej ${props.selectedUser?.firstname}</p>` +
            `<p><br /></p>` +
            `<p>Velkommen som bruger af arbejdsmiljøportalen Amorg.</p>` +
            `<p><br /></p>` +
            `<p>Før du kan logge ind, skal du oprette en adgangskode.</p>` +
            `<p>Det gør du ved at besøge dette link:</p>` +
            `<a href="${window.location.protocol}//${window.location.host}/request/setuppassword">${window.location.protocol}//${window.location.host}/request/setuppassword</a>` +
            `<p><br /></p>` +
            `<p>For at oprette en adgangskode skal du have adgang til følgende:</p>` + 
            `<p><br /></p>` +
            `<p>Den email du er oprettet med: ${props.selectedUser?.email || ''},</p>` +
            `<p>Din organisations id: ${props.siteId}</p>` +
            `<p><br /></p>` +
            `<p>Når du har oprettet en adgangskode, kan du logge ind og komme i gang med at bruge Amorg med det samme.</p>` +
            `<p><br /></p>` +
            `<p>Mange venlige hilsner fra din arbejdsmiljøorganisation</p>`
        );

        setShowModal(true);
    }

    async function handleClickSend()
    {
        if(!props.selectedUser)
            return;

        setLoading(true);
        await sendUserIntroEmail(props.selectedUser?.id || '', message);
        await refreshLastEmailSent();
        setLoading(false);

        setShowModal(false);
    }

    return (
        <div>
            <LoadingPageOverlay loading={loading} />

            <Flex flexDirection="column" gap={8}>
                <Button variant={lastEmailSent===null ? "filled" : "outlined"}
                    icon="interface_glyph_email"
                    text="Send velkomstemail"
                    onClick={handleClickOpen} />

                <Flex justifyContent="flex-end" alignItems="center" gap={8}>
                    {lastEmailSent &&
                        <Small>
                            Sidst sendt: {toDateTimeString(lastEmailSent)}
                        </Small>   
                    }
                    <ToggletipTheOne horizontalAlignment="right" verticalAlignment="top">
                        <p>Send en velkomstmail til brugeren med et link til at oprette en adgangskode.</p>
                        <p>Hvis brugeren allerede har modtaget en velkomstemail, kan du se hvornår den sidst blev sendt.</p>
                        <p>Du kan godt sende mere end en velkomstemail.</p>
                    </ToggletipTheOne>
                </Flex>
            </Flex>

            <Modal
                title="Send velkomstemail"
                showModal={showModal}
                onCancel={() => { setShowModal(false); }}
                content={

                    <Flex flexDirection="column" gap={16}>
                        <HtmlEditor
                            id="user-intro-email-message-editor"
                            value={message} 
                            onChange={(value) => setMessage(value)} 
                        />

                        <ButtonRow>
                            <Button 
                                variant="filled" 
                                text="Send email"
                                onClick={handleClickSend}
                            />
                        </ButtonRow>
                    </Flex>
                }
            />
        </div>
    );
}